import produce, { Draft } from 'immer';
import isPlainObject from 'lodash/isPlainObject';
import { isFSA, FluxStandardAction } from 'flux-standard-action';
import { Reducer } from 'redux';

type CreateReducerHandlers<S = any> = {
  [key: string]: (draftState: Draft<S>, action: FluxStandardAction) => void;
};

/**
 * Standard function to create Redux reducer.
 *
 * `createReducer` simplifies reducer creation and more importantly,
 * _it ensures immutability_. So direct modification to state object is allowed.
 * See https://github.com/immerjs/immer.
 *
 * Concretely, it turns reducer creation with a huge switch block into
 *
 * ```javascript
 * const initialState = {}
 * const reducer = createReducer(initialState, {
 *   ACTION_1: (draft, action) => {
 *     draft.count++ // direct modification is allowed
 *     return draft // returning is optional
 *   },
 *   ACTION_2: (draft, action) => {}
 * })
 * ```
 *
 * @param initialState Initial state of the reducer.
 * @param handlers An action-handler object hash. Each handler function receives `state` and `action`.
 * @return The reducer.
 */
function createReducer<S = any>(
  initialState: S,
  handlers: CreateReducerHandlers<S>
) {
  // Sanity checks
  if (!handlers) {
    throw new Error('Missing `handlers` in `createReducer`.');
  }
  if (!isPlainObject(handlers)) {
    throw new Error(
      `Expect \`handlers\` to be an object but got ${typeof handlers} instead.`
    );
  }

  // The reducer!!!
  // eslint-disable-next-line @typescript-eslint/default-param-last
  return function reducer(state = initialState, action) {
    // Flux Standard Action check
    // TODO: move this to middleware once we have all reducers converted
    if (!isFSA(action)) {
      const baseMessage = 'Reducer received a non-FSA-compliant action.';
      const suggestion =
        'Check https://github.com/redux-utilities/flux-standard-action for more details.';
      const currentValue = `Received action: ${JSON.stringify(action)}'`;
      throw new Error(`${baseMessage} ${suggestion} ${currentValue}`);
    }

    // Get the correct handler to handle action
    if (handlers.hasOwnProperty(action.type)) {
      const handler = handlers[action.type];
      return produce<S>(state, (draft) => handler(draft, action));
    }

    // If we get here, return existing state
    return state;
  } as Reducer<S>;
}

export default createReducer;
